<script setup lang="ts">
import { computed } from 'vue';
import { InputGroup, Link, Typography } from '@apparatix/library/core';
import TextTruncate from '@apparatix/library/TextTruncate.vue';
import DetailEditorBase from './DetailEditorBase.vue';
import type {
  AccountDetailErrors,
  AccountDetailSchema,
  MarketSchema,
} from '@apparatix/schemas/AccountDetail';
import type { PhoneNumberSchema } from '@apparatix/schemas/Addressable';
import { useAccountStore } from '@apparatix/stores/useAccountStore';
import { useActivities, useStringFunctions } from '@apparatix/composables';

type Props = {
  accountDetails: AccountDetailSchema;
  noHeadingLabels?: boolean;
  errors?: AccountDetailErrors;
  canChangeOwner?: boolean;
}

const props = defineProps<Props>();
const store = useAccountStore();
const { formattedUrl } = useStringFunctions();
const { postActivity } = useActivities();

const leftColLabel = computed(() => {
  if (props.noHeadingLabels) return undefined;
  return 'General';
});

const rightColLabel = computed(() =>
  'Get in Touch',
  // if (props.noHeadingLabels) return undefined;
  // return props.type === 'Lead' ? undefined : 'Details';
);

// @TODO: try adding these to respective Schema as methods
const workPhone = computed(() => props.accountDetails.phoneNumbers.find((p: PhoneNumberSchema) => p.addressType.toLowerCase() === 'work'));
const cellPhone = computed(() => props.accountDetails.phoneNumbers.find((p: PhoneNumberSchema) => p.addressType.toLowerCase() === 'cell'));
const faxPhone = computed(() => props.accountDetails.phoneNumbers.find((p: PhoneNumberSchema) => p.addressType.toLowerCase() === 'fax'));
const markets = computed(() => props.accountDetails.markets.map((m: MarketSchema) => m.market).join(', '));
const office = computed(() => props.accountDetails.office?.office);
const addressUrl = computed(() => 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(props.accountDetails.addresses[0]?.fullAddress));

</script>
<template>
  <DetailEditorBase :left-col-header="leftColLabel" :right-col-header="rightColLabel">
    <template #left-col>
      <InputGroup>
        <Typography variant="bold-label">
          Owner
        </Typography>
        <Typography variant="form-label">
          {{ props.accountDetails.owner.fullName }}
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Name
        </Typography>
        <Typography v-show="Boolean(props.accountDetails.name)" variant="form-label">
          {{ props.accountDetails.name }}
        </Typography>
        <Typography v-show="!Boolean(props.accountDetails.name)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Legal Name
        </Typography>
        <Typography variant="form-label">
          {{ props.accountDetails.legalName }}
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Account Source
        </Typography>
        <Typography v-show="Boolean(props.accountDetails.source.selection)" variant="form-label">
          {{ props.accountDetails.source.selection }}
        </Typography>
        <Typography v-show="!Boolean(props.accountDetails.source.selection)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Industry
        </Typography>
        <Typography variant="form-label">
          <TextTruncate :text="props.accountDetails.industry.description" />
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Label
        </Typography>
        <div v-if="Boolean(props.accountDetails.label?.value)" class="flex items-center gap-2">
          <span
            class="h-2.5 w-2.5 rounded-full inline-block"
            :style="{ backgroundColor: props.accountDetails.label?.color }"
          />
          <Typography variant="form-label">
            {{ props.accountDetails.label?.value }}
          </Typography>
        </div>
        <Typography v-show="!Boolean(props.accountDetails.label?.value)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Markets
        </Typography>
        <Typography v-show="Boolean(markets)" variant="form-label">
          {{ markets }}
        </Typography>
        <Typography v-show="!Boolean(markets)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <template v-if="store.options.authorizedOffices.length > 1">
        <InputGroup>
          <Typography variant="bold-label">
            Office
          </Typography>
          <Typography v-show="Boolean(office)" variant="form-label">
            {{ office }}
          </Typography>
          <Typography v-show="!Boolean(office)" muted variant="form-label">
            Empty
          </Typography>
        </InputGroup>
      </template>
    </template>
    <template v-if="true" #right-col>
      <InputGroup>
        <Typography variant="bold-label">
          Phone
        </Typography>
        <Link v-show="Boolean(workPhone?.number)" :href="'tel:' + workPhone?.number || ''" @pointerdown="postActivity(2, props.accountDetails.id, props.accountDetails.name, 'accounts')">
          {{ workPhone?.number || '' }}
        </Link>
        <Typography v-show="!Boolean(workPhone?.number)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Cell
        </Typography>
        <Link v-show="Boolean(cellPhone?.number)" :href="'tel:' + cellPhone?.number || ''" @pointerdown="postActivity(2, props.accountDetails.id, props.accountDetails.name, 'accounts')">
          {{ cellPhone?.number || '' }}
        </Link>
        <Typography v-show="!Boolean(cellPhone?.number)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Fax
        </Typography>
        <Link v-show="Boolean(faxPhone?.number)" :href="'tel:' + faxPhone?.number || ''" @pointerdown="postActivity(2, props.accountDetails.id, props.accountDetails.name, 'accounts')">
          {{ faxPhone?.number || '' }}
        </Link>
        <Typography v-show="!Boolean(faxPhone?.number)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Website
        </Typography>
        <Link
          v-show="Boolean(props.accountDetails.webPages[0]?.url)"
          :href="formattedUrl(props.accountDetails.webPages[0]?.url ?? '')"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ props.accountDetails.webPages[0]?.url ?? '' }}
        </Link>
        <Typography v-show="!Boolean(props.accountDetails.webPages[0]?.url)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
      <InputGroup>
        <div class="flex items-center w-full pb-3 lg:pb-4 border-b border-[var(--p-content-border-color)]">
          <Typography variant="form-heading">
            Location
          </Typography>
        </div>
      </InputGroup>
      <InputGroup>
        <Typography variant="bold-label">
          Address
        </Typography>
        <!--@TODO: We should modify the component to allow address formatting-->
        <Link
          v-show="Boolean(store.accountDetails.addresses[0]?.fullAddress)"
          :href="addressUrl"
          class="whitespace-pre-line"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ store.accountDetails.addresses[0]?.fullAddress ?? '' }}
        </Link>
        <Typography v-show="!Boolean(store.accountDetails.addresses[0]?.fullAddress)" muted variant="form-label">
          Empty
        </Typography>
      </InputGroup>
    </template>
  </DetailEditorBase>
</template>
